import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AnimatePresence,
  motion,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { ArrowDown, ArrowUpRight, Compass, MapPin } from "lucide-react";
import SEO from "../components/SEO";
import SplitText from "../components/SplitText";
import { usePageContent } from "../lib/usePageContent";
import { resolveImage } from "../lib/data";

/* ----------------------------- helpers ----------------------------- */

/** Render *word* → editorial serif italic emphasis. */
function italicize(text = "") {
  const out = [];
  const parts = String(text).split(/(\*[^*]+\*)/g);
  parts.forEach((p, i) => {
    if (!p) return;
    if (p.startsWith("*") && p.endsWith("*")) {
      out.push(
        <em
          key={i}
          className="font-serif italic text-[#C48F56]"
          style={{ fontWeight: 400, fontFeatureSettings: "'ss01' 1" }}
        >
          {p.slice(1, -1)}
        </em>
      );
    } else {
      out.push(<span key={i}>{p}</span>);
    }
  });
  return out;
}

const DEFAULTS = {
  eyebrow: "The Cathedral · Duomo · Milano",
  title_line_1: "Dove",
  title_line_2: "la *luce*",
  title_line_3: "incontra",
  title_line_4: "la *pietra.*",
  hero_subtitle: "",
  hero_meta_left: "Piazza Duomo · Milano",
  hero_meta_right: "Apertura Q4 2027",
  hero_image: "",
  hero_scroll_label: "Scorri per entrare",
  poster_image: "",
  poster_eyebrow: "L'indirizzo",
  poster_caption: "Piazza Duomo. *Il centro del centro.*",
  overture_eyebrow: "Ouverture",
  overture_words: ["Restauro.", "Rito.", "Rigore.", "Rinascita."],
  overture_paragraph: "",
  manifesto_eyebrow: "Manifesto",
  manifesto_line_1: "",
  manifesto_line_2: "",
  manifesto_line_3: "",
  manifesto_body: "",
  numbers_eyebrow: "Cifre",
  numbers_title: "La grammatica del progetto.",
  num_1_value: "5",
  num_1_label: "Stelle",
  num_2_value: "68",
  num_2_label: "Camere & Suite",
  num_3_value: "0",
  num_3_label: "Metri dal Duomo",
  num_4_value: "2027",
  num_4_label: "Anno di apertura",
  location_eyebrow: "Coordinate",
  location_title: "",
  location_paragraph_1: "",
  location_paragraph_2: "",
  location_landmarks: [],
  chapters: [],
  timeline_eyebrow: "Roadmap",
  timeline_title: "Dalla firma alla chiave.",
  timeline: [],
  quote_body: "",
  quote_author: "",
  cta_eyebrow: "Riservato investitori",
  cta_title: "",
  cta_description: "",
  cta_button_label: "Area investitori",
  cta_button_url: "/investor",
  credits_eyebrow: "Credits",
  credits: [],
};

/* ----------------------------- page ----------------------------- */

export default function TheCathedral() {
  const c = usePageContent("the-cathedral", DEFAULTS);
  const rootRef = useRef(null);

  // Global scroll progress bar dedicato alla pagina
  const { scrollYProgress } = useScroll();
  const scrollBar = useSpring(scrollYProgress, {
    stiffness: 90,
    damping: 30,
    mass: 0.3,
  });

  return (
    <div
      ref={rootRef}
      data-testid="the-cathedral-page"
      className="relative bg-[#0A0908] text-white overflow-hidden"
    >
      <SEO
        title={`The Cathedral Duomo · ${c.hero_meta_left || "Milano"}`}
        description={c.hero_subtitle || DEFAULTS.overture_paragraph}
      />

      {/* Grain texture overlay for cinematic feel */}
      <GrainOverlay />

      {/* Custom minimal top progress line */}
      <motion.div
        style={{ scaleX: scrollBar, transformOrigin: "0% 50%" }}
        className="fixed top-0 left-0 right-0 h-[2px] bg-[#C48F56] z-[60]"
      />

      <Hero c={c} />
      <Statement c={c} />
      <Poster c={c} />
      <Overture c={c} />
      <Manifesto c={c} />
      <Numbers c={c} />
      <Chapters c={c} />
      <LocationBlock c={c} />
      <Timeline c={c} />
      <Quote c={c} />
      <CTA c={c} />
      <Credits c={c} />
    </div>
  );
}

/* ============================ HERO ============================ */

function Hero({ c }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "-6%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

  const heroImg =
    resolveImage(c.hero_image) || "/brand/cathedral/cathedral-hero.jpg";

  return (
    <section
      ref={ref}
      data-testid="cathedral-hero"
      className="relative min-h-[100svh] flex flex-col overflow-hidden bg-[#0A0908]"
    >
      {/* Full-bleed cinematic image — the star of the hero */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 will-change-transform"
      >
        <img
          src={heroImg}
          alt="The Cathedral Hotel — Piazza Duomo"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        {/* Minimal gradient wash for text legibility on edges only */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0908]/60 via-transparent to-[#0A0908]/70" />
      </motion.div>

      {/* Corner brackets (editorial frame) */}
      <CornerBracket className="top-6 left-6 md:top-10 md:left-10" />
      <CornerBracket className="top-6 right-6 md:top-10 md:right-10" rotate={90} />
      <CornerBracket className="bottom-6 left-6 md:bottom-10 md:left-10" rotate={-90} />
      <CornerBracket className="bottom-6 right-6 md:bottom-10 md:right-10" rotate={180} />

      {/* Top-left eyebrow */}
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="relative z-10 pt-28 md:pt-36 px-8 md:px-16"
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          data-testid="cathedral-eyebrow"
          className="flex items-center gap-3 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.4em] text-white/90"
        >
          <span className="h-px w-8 bg-[#C48F56]" />
          <span>{c.eyebrow}</span>
        </motion.div>
      </motion.div>

      {/* Spacer that lets the image breathe */}
      <div className="flex-1" />

      {/* Bottom band: subtitle + meta */}
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="relative z-10 pb-24 md:pb-32 px-8 md:px-16 max-w-[1440px] w-full mx-auto"
      >
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="max-w-xl text-base md:text-lg text-white/85 leading-relaxed"
        >
          {c.hero_subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-10 md:mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.3em] text-white/85"
        >
          <span className="inline-flex items-center gap-2">
            <MapPin size={12} className="text-[#C48F56]" /> {c.hero_meta_left}
          </span>
          <span className="w-6 h-px bg-white/40" />
          <span>{c.hero_meta_right}</span>
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.6 }}
        className="absolute bottom-6 md:bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/70 z-10"
      >
        <span className="font-mono text-[9px] uppercase tracking-[0.35em]">
          {c.hero_scroll_label}
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown size={16} />
        </motion.div>
      </motion.div>
    </section>
  );
}

/* ============================ POSTER (full-bleed atmospheric band) ============================ */

function Poster({ c }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const img =
    resolveImage(c.poster_image) || "/brand/cathedral/poster-piazza.jpg";

  return (
    <section
      ref={ref}
      data-testid="cathedral-poster"
      className="relative h-[70svh] md:h-[85svh] overflow-hidden bg-black"
    >
      <motion.div style={{ y }} className="absolute inset-0 will-change-transform">
        <img
          src={img}
          alt={c.poster_caption || "Piazza Duomo · Milano"}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0908]/40 via-transparent to-[#0A0908]/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0908]/50 via-transparent to-transparent" />
      </motion.div>

      {/* Editorial caption */}
      <div className="relative z-10 h-full flex flex-col justify-end p-8 md:p-16 max-w-[1440px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1 }}
          className="max-w-lg"
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#C48F56] mb-4 flex items-center gap-3">
            <span className="h-px w-8 bg-[#C48F56]" />
            {c.poster_eyebrow || "L'indirizzo"}
          </div>
          <div className="font-display font-light text-3xl md:text-5xl leading-[1.05] tracking-tightest text-white">
            {italicize(c.poster_caption || "Piazza Duomo. *Il centro del centro.*")}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ============================ STATEMENT (diagonal typography) ============================ */

function Statement({ c }) {
  return (
    <section
      data-testid="cathedral-statement"
      className="relative py-32 md:py-56 overflow-hidden bg-[#0A0908]"
    >
      <div className="max-w-[1440px] mx-auto px-8 md:px-16">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#C48F56] mb-12 md:mb-16"
        >
          Statement
        </motion.p>

        <h1 className="font-display font-light leading-[0.9] tracking-tightest text-[14vw] md:text-[10vw] lg:text-[8.5vw] max-w-[95vw]">
          <SplitText text={c.title_line_1} className="block" delay={0} stagger={0.02} />
          <SplitText text={c.title_line_2} className="block ml-[15%] md:ml-[22%]" delay={0.12} stagger={0.02} />
          <SplitText text={c.title_line_3} className="block ml-[5%] md:ml-[8%]" delay={0.24} stagger={0.02} />
          <SplitText text={c.title_line_4} className="block ml-[25%] md:ml-[38%]" delay={0.36} stagger={0.02} />
        </h1>
      </div>
    </section>
  );
}

function CornerBracket({ className = "", rotate = 0 }) {
  return (
    <div
      className={`absolute w-6 h-6 md:w-8 md:h-8 pointer-events-none ${className}`}
      style={{ transform: `rotate(${rotate}deg)` }}
    >
      <div className="absolute top-0 left-0 w-full h-px bg-[#C48F56]/70" />
      <div className="absolute top-0 left-0 w-px h-full bg-[#C48F56]/70" />
    </div>
  );
}

/* ============================ OVERTURE (rotating word manifesto) ============================ */

function Overture({ c }) {
  const words =
    Array.isArray(c.overture_words) && c.overture_words.length
      ? c.overture_words
      : DEFAULTS.overture_words;
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % words.length), 1600);
    return () => clearInterval(t);
  }, [words.length]);

  return (
    <section
      data-testid="cathedral-overture"
      className="relative py-32 md:py-48 border-t border-white/5"
    >
      <div className="max-w-[1440px] mx-auto px-8 md:px-16 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#C48F56]">
            {c.overture_eyebrow}
          </div>
        </div>
        <div className="md:col-span-9">
          <div className="font-display font-light text-6xl md:text-8xl lg:text-9xl leading-[0.95] tracking-tightest h-[1.1em] overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={words[i]}
                initial={{ y: "100%", opacity: 0 }}
                animate={{ y: "0%", opacity: 1 }}
                exit={{ y: "-100%", opacity: 0 }}
                transition={{ duration: 0.65, ease: [0.2, 0.7, 0.2, 1] }}
                className="font-serif italic text-[#C48F56]"
                style={{ fontWeight: 400 }}
              >
                {words[i]}
              </motion.div>
            </AnimatePresence>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1 }}
            className="mt-12 md:mt-16 max-w-2xl text-lg md:text-xl text-white/70 leading-relaxed"
          >
            {c.overture_paragraph}
          </motion.p>
        </div>
      </div>
    </section>
  );
}

/* ============================ MANIFESTO ============================ */

function Manifesto({ c }) {
  return (
    <section
      data-testid="cathedral-manifesto"
      className="relative py-32 md:py-56 border-t border-white/5 overflow-hidden"
    >
      {/* Big background type ghost */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 flex items-center justify-center"
      >
        <div className="font-serif italic text-[38vw] leading-none tracking-tightest text-white/[0.035] select-none" style={{ fontWeight: 400 }}>
          Duomo
        </div>
      </div>

      <div className="relative max-w-[1440px] mx-auto px-8 md:px-16">
        <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#C48F56] mb-10">
          {c.manifesto_eyebrow}
        </div>

        <RevealBlock delay={0}>
          <div className="font-display font-light text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tightest">
            <div>{italicize(c.manifesto_line_1)}</div>
            <div className="mt-2">{italicize(c.manifesto_line_2)}</div>
            <div className="mt-2">{italicize(c.manifesto_line_3)}</div>
          </div>
        </RevealBlock>

        <RevealBlock delay={0.15}>
          <p className="mt-14 max-w-2xl text-base md:text-lg text-white/70 leading-relaxed">
            {c.manifesto_body}
          </p>
        </RevealBlock>
      </div>
    </section>
  );
}

/* ============================ NUMBERS ============================ */

function Numbers({ c }) {
  const items = [
    { v: c.num_1_value, l: c.num_1_label },
    { v: c.num_2_value, l: c.num_2_label },
    { v: c.num_3_value, l: c.num_3_label },
    { v: c.num_4_value, l: c.num_4_label },
  ];

  return (
    <section
      data-testid="cathedral-numbers"
      className="relative py-28 md:py-40 border-t border-white/5"
    >
      <div className="max-w-[1440px] mx-auto px-8 md:px-16">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-16 md:mb-20">
          <div className="md:col-span-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#C48F56]">
              {c.numbers_eyebrow}
            </div>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-display font-light text-4xl md:text-6xl leading-[1] tracking-tightest">
              {italicize(c.numbers_title)}
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-14 md:gap-14">
          {items.map((it, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.08 }}
              className="relative"
            >
              <div className="absolute -top-4 left-0 font-mono text-[10px] text-white/30 tracking-[0.3em]">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="font-display font-light text-[19vw] md:text-[9vw] lg:text-[7vw] leading-none tracking-tightest">
                {it.v}
              </div>
              <div className="mt-4 h-px bg-white/15" />
              <div className="mt-4 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-white/60">
                {it.l}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================ CHAPTERS ============================ */

function Chapters({ c }) {
  const chapters = Array.isArray(c.chapters) ? c.chapters : [];
  if (!chapters.length) return null;

  return (
    <section
      data-testid="cathedral-chapters"
      className="relative border-t border-white/5"
    >
      {chapters.map((ch, i) => (
        <Chapter key={i} chapter={ch} index={i} last={i === chapters.length - 1} />
      ))}
    </section>
  );
}

function Chapter({ chapter, index, last }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1]);
  const flip = index % 2 === 1;

  return (
    <div
      ref={ref}
      className={`relative py-24 md:py-40 ${
        !last ? "border-b border-white/5" : ""
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-8 md:px-16 grid md:grid-cols-12 gap-10 items-center">
        {/* Text column */}
        <div className={`md:col-span-5 ${flip ? "md:order-2" : ""}`}>
          <div className="font-serif italic text-[#C48F56] text-7xl md:text-8xl leading-none tracking-tightest" style={{ fontWeight: 400 }}>
            {chapter.index}
          </div>
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="mt-8 font-display font-light text-3xl md:text-5xl leading-[1.05] tracking-tightest"
          >
            {chapter.title}
          </motion.h3>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="mt-6 text-white/65 leading-relaxed max-w-md text-base md:text-lg"
          >
            {chapter.body}
          </motion.p>
        </div>

        {/* Image column */}
        <div className={`md:col-span-7 ${flip ? "md:order-1" : ""}`}>
          <motion.div
            initial={{ opacity: 0, clipPath: "inset(20% 0% 20% 0%)" }}
            whileInView={{ opacity: 1, clipPath: "inset(0% 0% 0% 0%)" }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.3, ease: [0.2, 0.7, 0.2, 1] }}
            className="relative aspect-[4/3] md:aspect-[16/11] overflow-hidden rounded-sm bg-white/5"
          >
            <motion.img
              src={resolveImage(chapter.image) || chapter.image}
              alt={chapter.title}
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 w-full h-full object-cover will-change-transform"
              loading="lazy"
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
          </motion.div>
        </div>
      </div>
    </div>
  );
}

/* ============================ LOCATION ============================ */

function LocationBlock({ c }) {
  const landmarks = Array.isArray(c.location_landmarks)
    ? c.location_landmarks
    : [];

  return (
    <section
      data-testid="cathedral-location"
      className="relative py-32 md:py-48 border-t border-white/5 overflow-hidden"
    >
      <div className="max-w-[1440px] mx-auto px-8 md:px-16 grid md:grid-cols-12 gap-14">
        <div className="md:col-span-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#C48F56] flex items-center gap-3">
            <Compass size={13} /> {c.location_eyebrow}
          </div>
          <h2 className="mt-8 font-display font-light text-4xl md:text-6xl leading-[1.02] tracking-tightest">
            {italicize(c.location_title)}
          </h2>
          <p className="mt-8 text-white/65 leading-relaxed">
            {c.location_paragraph_1}
          </p>
          <p className="mt-4 text-white/65 leading-relaxed">
            {c.location_paragraph_2}
          </p>
        </div>

        <div className="md:col-span-7">
          <RadialMap landmarks={landmarks} />
        </div>
      </div>
    </section>
  );
}

function RadialMap({ landmarks }) {
  // Cinematic "compass" list — not a real map, but an editorial abstraction.
  return (
    <div className="relative aspect-square md:aspect-[5/4] w-full max-w-[640px] mx-auto">
      {/* concentric rings */}
      <div className="absolute inset-0 flex items-center justify-center">
        {[0.35, 0.55, 0.78, 1].map((s, i) => (
          <div
            key={i}
            className="absolute rounded-full border border-white/8"
            style={{
              width: `${s * 100}%`,
              height: `${s * 100}%`,
            }}
          />
        ))}
      </div>
      {/* crosshair */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-full h-px bg-white/8" />
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="h-full w-px bg-white/8" />
      </div>
      {/* center point (THE hotel) */}
      <motion.div
        initial={{ scale: 0 }}
        whileInView={{ scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.2 }}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"
      >
        <div className="w-3 h-3 rounded-full bg-[#C48F56] shadow-[0_0_0_8px_rgba(196,143,86,0.15)]" />
        <div className="mt-3 font-mono text-[9px] uppercase tracking-[0.3em] text-white/70">
          The Cathedral
        </div>
      </motion.div>

      {/* landmark labels: distributed on a circle */}
      {landmarks.slice(0, 8).map((lm, i) => {
        const n = Math.max(landmarks.length, 3);
        const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
        const radius = 44; // % from center
        const x = 50 + Math.cos(angle) * radius;
        const y = 50 + Math.sin(angle) * radius;
        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.7 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 + i * 0.08 }}
            style={{ left: `${x}%`, top: `${y}%` }}
            className="absolute -translate-x-1/2 -translate-y-1/2 text-center"
          >
            <div className="w-1.5 h-1.5 mx-auto rounded-full bg-white/70" />
            <div className="mt-2 font-mono text-[9px] md:text-[10px] uppercase tracking-[0.2em] text-white/80 whitespace-nowrap">
              {lm.label}
            </div>
            <div className="mt-0.5 font-mono text-[9px] text-[#C48F56]">
              {lm.distance}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

/* ============================ TIMELINE ============================ */

function Timeline({ c }) {
  const items = Array.isArray(c.timeline) ? c.timeline : [];
  if (!items.length) return null;

  return (
    <section
      data-testid="cathedral-timeline"
      className="relative py-32 md:py-48 border-t border-white/5"
    >
      <div className="max-w-[1440px] mx-auto px-8 md:px-16">
        <div className="grid md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#C48F56]">
              {c.timeline_eyebrow}
            </div>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-display font-light text-4xl md:text-6xl leading-[1.02] tracking-tightest">
              {italicize(c.timeline_title)}
            </h2>
          </div>
        </div>

        <div className="relative">
          {/* vertical rail */}
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-white/10" />

          <div className="space-y-14 md:space-y-24">
            {items.map((step, i) => {
              const flip = i % 2 === 1;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.9, delay: i * 0.05 }}
                  className="relative"
                >
                  {/* Dot on rail */}
                  <div className="absolute left-4 md:left-1/2 -translate-x-1/2 top-1.5 w-3 h-3 rounded-full bg-[#C48F56] ring-4 ring-[#0A0908] z-10" />

                  {/* Row */}
                  <div className="pl-12 md:pl-0 md:grid md:grid-cols-2 md:gap-16">
                    <div className={flip ? "md:col-start-2" : "md:text-right"}>
                      <TimelineCard step={step} align={flip ? "left" : "right"} />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function TimelineCard({ step, align = "left" }) {
  return (
    <div className={align === "right" ? "md:text-right" : ""}>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C48F56]">
        {step.date}
      </div>
      <div className="mt-3 font-display text-xl md:text-2xl leading-tight">
        {step.title}
      </div>
      <p className="mt-2 text-white/60 text-sm leading-relaxed max-w-md md:inline-block">
        {step.body}
      </p>
    </div>
  );
}

/* ============================ QUOTE ============================ */

function Quote({ c }) {
  if (!c.quote_body) return null;
  return (
    <section
      data-testid="cathedral-quote"
      className="relative py-32 md:py-48 border-t border-white/5"
    >
      <div className="max-w-[1440px] mx-auto px-8 md:px-16">
        <motion.blockquote
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1 }}
          className="max-w-4xl"
        >
          <div
            aria-hidden
            className="font-display text-[#C48F56] text-8xl md:text-9xl leading-none mb-6"
          >
            &ldquo;
          </div>
          <p className="font-serif italic font-light text-3xl md:text-5xl leading-[1.2] tracking-tight" style={{ fontWeight: 400 }}>
            {c.quote_body}
          </p>
          <footer className="mt-10 font-mono text-[10px] uppercase tracking-[0.35em] text-white/50">
            — {c.quote_author}
          </footer>
        </motion.blockquote>
      </div>
    </section>
  );
}

/* ============================ CTA ============================ */

function CTA({ c }) {
  const url = c.cta_button_url || "/investor";
  const external = /^https?:\/\//.test(url);

  return (
    <section
      data-testid="cathedral-cta"
      className="relative py-28 md:py-40 border-t border-white/5"
    >
      <div className="max-w-[1440px] mx-auto px-8 md:px-16">
        <div className="rounded-2xl border border-white/10 bg-[#0F0D0B] p-8 md:p-16 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#C48F56]">
              {c.cta_eyebrow}
            </div>
            <h3 className="mt-4 font-display font-light text-3xl md:text-5xl leading-[1.05] tracking-tightest">
              {italicize(c.cta_title)}
            </h3>
            <p className="mt-5 text-white/60 max-w-xl leading-relaxed">
              {c.cta_description}
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            {external ? (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cathedral-cta-button"
                className="inline-flex items-center gap-3 rounded-full bg-white text-black px-7 py-3.5 text-sm uppercase tracking-[0.25em] hover:bg-[#C48F56] transition-colors"
              >
                {c.cta_button_label}
                <ArrowUpRight size={16} />
              </a>
            ) : (
              <Link
                to={url}
                data-testid="cathedral-cta-button"
                className="inline-flex items-center gap-3 rounded-full bg-white text-black px-7 py-3.5 text-sm uppercase tracking-[0.25em] hover:bg-[#C48F56] transition-colors"
              >
                {c.cta_button_label}
                <ArrowUpRight size={16} />
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================ CREDITS ============================ */

function Credits({ c }) {
  const items = Array.isArray(c.credits) ? c.credits : [];
  if (!items.length) return null;

  return (
    <section
      data-testid="cathedral-credits"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <div className="max-w-[1440px] mx-auto px-8 md:px-16">
        <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#C48F56] mb-10">
          {c.credits_eyebrow}
        </div>
        <div className="grid md:grid-cols-3 gap-y-6 gap-x-14">
          {items.map((it, i) => (
            <div
              key={i}
              className="flex items-baseline justify-between border-b border-white/8 pb-3"
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/45">
                {it.label}
              </span>
              <span className="text-white/85 text-sm text-right">
                {it.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================ REVEAL HELPER ============================ */

function RevealBlock({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, filter: "blur(6px)" }}
      whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 1.1, delay, ease: [0.2, 0.7, 0.2, 1] }}
    >
      {children}
    </motion.div>
  );
}

/* ============================ GRAIN OVERLAY ============================ */

function GrainOverlay() {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[55] opacity-[0.06] mix-blend-overlay"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E\")",
        backgroundSize: "180px 180px",
      }}
    />
  );
}
